import { useCallback, useState } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { getProto } from '../../../im/pb-imports';
import { ImManager } from '../../../im/imManager';
import type { ImClient } from '../../../im/imClient';
import { chatRepository } from '../../../db/repository';
import type { DirectoryMap, GroupMap, SessionMap, SessionKind } from '@domain/chat/types';
import type { ConnState, LogEntry } from '@shared/config/runtime';
import { getErrorMessage } from '@shared/utils/ui';

export type CreateGroupDraft = {
  name: string;
  memberIds: number[];
};

type UseWorkspaceGroupsOptions = {
  connState: ConnState;
  pushLog: (msg: string, kind?: LogEntry['kind']) => void;
  imRef: MutableRefObject<ImClient | null>;
  imUserIdRef: MutableRefObject<number | null>;
  contactsRef: MutableRefObject<DirectoryMap>;
  groupsRef: MutableRefObject<GroupMap>;
  setGroupsById: Dispatch<SetStateAction<GroupMap>>;
  setSessionsByKey: Dispatch<SetStateAction<SessionMap>>;
  setIsCreateGroupModalOpen: Dispatch<SetStateAction<boolean>>;
  openGroupSession: (groupId: number) => void;
  sessionKeyOf: (sessionType: SessionKind, sessionId: number) => string;
  resolveSessionPresentation: (
    sessionType: SessionKind,
    sessionId: number,
    contactsById: DirectoryMap,
    groupsById: GroupMap,
  ) => {
    title: string;
    subtitle: string;
    avatarText: string;
  };
};

export function useWorkspaceGroups({
  connState,
  pushLog,
  imRef,
  imUserIdRef,
  contactsRef,
  groupsRef,
  setGroupsById,
  setSessionsByKey,
  setIsCreateGroupModalOpen,
  openGroupSession,
  sessionKeyOf,
  resolveSessionPresentation,
}: UseWorkspaceGroupsOptions) {
  const [createGroupDraft, setCreateGroupDraft] = useState<CreateGroupDraft>({ name: '', memberIds: [] });
  const [createGroupSubmitting, setCreateGroupSubmitting] = useState(false);
  const [groupDetailLoading, setGroupDetailLoading] = useState(false);
  const [groupDetailError, setGroupDetailError] = useState<string | null>(null);

  const resetGroupUiState = useCallback(() => {
    setCreateGroupDraft({ name: '', memberIds: [] });
    setCreateGroupSubmitting(false);
    setGroupDetailLoading(false);
    setGroupDetailError(null);
  }, []);

  const handleToggleCreateGroupMember = useCallback((memberId: number) => {
    setCreateGroupDraft((prev) => {
      const exists = prev.memberIds.includes(memberId);
      return {
        ...prev,
        memberIds: exists
          ? prev.memberIds.filter((id) => id !== memberId)
          : [...prev.memberIds, memberId],
      };
    });
  }, []);

  const syncGroupInfo = useCallback(async (groupIds: number[]) => {
    const client = imRef.current;
    const userId = imUserIdRef.current;
    const uniqueIds = [...new Set(groupIds.filter((id) => id > 0))];

    if (!client || userId == null || uniqueIds.length === 0) {
      throw new Error('当前无法更新群组信息');
    }

    const response = await client.requestGroupInfoByIds(userId, uniqueIds);
    const mappedGroups: GroupMap = {};

    for (const groupInfo of response.getGroupInfoListList()) {
      const groupId = Number(groupInfo.getGroupId?.() ?? 0);
      if (groupId <= 0) {
        continue;
      }

      const rawMemberIds = groupInfo.getGroupMemberListList?.() ?? [];
      const memberIds = Array.isArray(rawMemberIds)
        ? [...new Set(rawMemberIds
          .map((memberId) => Number(memberId))
          .filter((memberId) => Number.isFinite(memberId) && memberId > 0))]
        : [];

      mappedGroups[groupId] = {
        id: groupId,
        name: groupInfo.getGroupName?.() || `群组 #${groupId}`,
        avatarUrl: groupInfo.getGroupAvatar?.() || '',
        shieldStatus: groupInfo.getShieldStatus?.() || 0,
        memberIds,
      };
    }

    const groups = Object.values(mappedGroups);
    if (groups.length === 0) {
      throw new Error('服务端未返回群组信息');
    }

    await chatRepository.saveGroups(groups.map((group) => ({
      id: group.id,
      name: group.name,
      avatar: group.avatarUrl,
      shieldStatus: group.shieldStatus,
      memberIds: group.memberIds,
    })));

    const manager = ImManager.getInstance();
    for (const group of groups) {
      manager.groupMap.set(group.id, {
        id: group.id,
        name: group.name,
        avatar: group.avatarUrl,
        shieldStatus: group.shieldStatus,
        memberIds: group.memberIds,
      });
    }

    const mergedGroups = { ...groupsRef.current, ...mappedGroups };
    groupsRef.current = mergedGroups;
    setGroupsById((prev) => ({ ...prev, ...mappedGroups }));
    setSessionsByKey((prev) => {
      const next = { ...prev };
      for (const group of groups) {
        const nextSessionKey = sessionKeyOf('group', group.id);
        const session = next[nextSessionKey];
        if (!session) {
          continue;
        }
        const presentation = resolveSessionPresentation('group', group.id, contactsRef.current, mergedGroups);
        next[nextSessionKey] = {
          ...session,
          title: presentation.title,
          subtitle: presentation.subtitle,
          avatarText: presentation.avatarText,
        };
      }
      return next;
    });
  }, [contactsRef, groupsRef, imRef, imUserIdRef, resolveSessionPresentation, sessionKeyOf, setGroupsById, setSessionsByKey]);

  const refreshGroupDetail = useCallback(async (groupId: number) => {
    setGroupDetailLoading(true);
    setGroupDetailError(null);
    try {
      await syncGroupInfo([groupId]);
    } catch (error) {
      setGroupDetailError(getErrorMessage(error));
      throw error;
    } finally {
      setGroupDetailLoading(false);
    }
  }, [syncGroupInfo]);

  const handleCreateGroup = useCallback(async () => {
    const client = imRef.current;
    const userId = imUserIdRef.current;
    const groupName = createGroupDraft.name.trim();
    const memberIds = [...new Set(createGroupDraft.memberIds.filter((id) => id > 0 && id !== userId))];

    if (!client || connState !== 'online' || userId == null) {
      pushLog('实时连接未在线，无法创建群组', 'warn');
      return;
    }
    if (!groupName) {
      pushLog('请输入群组名称', 'warn');
      return;
    }
    if (memberIds.length === 0) {
      pushLog('请至少选择一位成员', 'warn');
      return;
    }

    setCreateGroupSubmitting(true);
    try {
      const proto = getProto();
      const response = await client.createGroup(
        userId,
        groupName,
        [userId, ...memberIds],
        '',
        proto.IM.BaseDefine.GroupType.GROUP_TYPE_TMP,
      );
      const groupId = response.getGroupId?.() ?? 0;
      await ImManager.getInstance().initData();
      setIsCreateGroupModalOpen(false);
      setCreateGroupDraft({ name: '', memberIds: [] });
      pushLog(`群组已创建：${groupName}`, 'success');
      if (groupId > 0) {
        openGroupSession(groupId);
      }
    } catch (error) {
      pushLog(`创建群组失败: ${getErrorMessage(error)}`, 'error');
    } finally {
      setCreateGroupSubmitting(false);
    }
  }, [connState, createGroupDraft.memberIds, createGroupDraft.name, imRef, imUserIdRef, openGroupSession, pushLog, setIsCreateGroupModalOpen]);

  return {
    createGroupDraft,
    setCreateGroupDraft,
    createGroupSubmitting,
    groupDetailLoading,
    groupDetailError,
    setGroupDetailError,
    resetGroupUiState,
    handleToggleCreateGroupMember,
    syncGroupInfo,
    refreshGroupDetail,
    handleCreateGroup,
  };
}
